import i18n from "i18n"

// Action types that have a success message (looked up from locales/en/messages.json).
// If the value is `true`, the key is used to look up `messages:success.<key>`.
// If the value is `null`, no message is shown for that action.
const successMessage = {
  // Analysis
  "analyses/setMovable": true,
  "analyses/moveIntoPlan": true,
  "analyses/fetchUnplanned": null,
  "analyses/fetchUnassignedSamples": null,
  "analyses/fetchPlanned": null,
  "analyses/remove": true,

  // AnalysisDefinition
  "analysisDefinitions/fetch": null,
  "analysisDefinitions/create": true,
  "analysisDefinitions/edit": true,
  "analysisDefinitions/remove": true,
  "analysisDraft/create": true,
  "analysisDraft/edit": true,

  // Samples
  "samples/delete-unlocked": true,

  // Asset
  "assets/fetchGraphInfo": null,
  "assets/remove": true,
  "assets/fetch": null,
  "assets/create": true,
  "assets/edit": true,

  // Auth
  "auth/login": null,
  "auth/logout": null,

  // CompetenceGroup
  "competenceGroups/fetch": null,
  "competenceGroups/create": true,
  "competenceGroups/edit": true,
  "competenceGroups/remove": true,

  // Iap
  "iap/run": true,
  "iap/runFillInSamples": true,

  // Integration Sheets
  "integrationSheets/fetchActionSheets": null,
  "integrationSheets/fetchProjectTemplateSheets": null,
  "integrationSheets/fetchSampleSheets": null,
  "integrationSheets/createActionSheet": true,
  "integrationSheets/createProjectTemplateSheets": true,
  "integrationSheets/createSampleSheet": true,
  "integrationSheets/editActionSheet": true,
  "integrationSheets/editProjectTemplateSheet": true,
  "integrationSheets/editSampleSheet": true,
  "integrationSheets/removeActionSheet": true,
  "integrationSheets/removeProjectTemplateSheet": true,
  "integrationSheets/removeSampleSheet": true,
  "integrationSheets/saveSapConversionSheet": true,

  // Loading
  "loading/fetchAll": null,

  // Member
  "members/fetch": null,
  "members/create": true,
  "members/createAsAdmin": true,
  "members/edit": true,
  "members/archive": true,

  // Metrics
  "metrics/fetch": null,

  // ProjectAction
  "projectActions/fetchUnplanned": null,
  "projectActions/fetchPlanned": null,
  "projectActions/editPlanned": true,
  "projectActions/move": true,
  "projectActions/setMovable": true,
  "projectActions/setLocation": true,
  "projectActions/setLocationUnplanned": true,
  "projectActions/editUnplanned": true,
  "projectActions/removePlanned": true,

  // Project
  "projects/fetch": null,
  "projects/create": true,
  "projects/edit": true,
  "projects/remove": true,
  "projects/delete-unplanned": true,
  "projects/lockProject": true,
  "projects/unlockProject": true,

  // ProjectTemplate
  "projectTemplates/fetch": null,
  "projectTemplates/create": true,
  "projectTemplates/createDraft": true,
  "projectTemplates/edit": true,
  "projectTemplates/editDraft": true,
  "projectTemplates/remove": true,

  // White tasks
  "whiteTasks/remove": true,
  "whiteTasks/create": true,
  "whiteTasks/fetch": null,
  "whiteTasks/edit": true,
  "whiteTasks/move": null,
  "whiteTasks/createRecurrence": true,
  "whiteTasks/editRecurrence": null,
  "whiteTasks/removeRecurrence": true,

  // Analysis tasks
  "analysisTasks/fetch": null,
  "analysisTasks/edit": true,
  "analysisTasks/move": null,

  // TeamStatus
  "teamStatus/fetchCurrent": null,

  // CustomerAdministration
  "customers/addCustomer": true,
  "customers/editCustomer": true,
  "customers/deleteCustomer": true,

  // Token
  "team/create-api-token": true,
  "team/delete-api-token": true,
}

/**
 * Look up Translated success message for the given action type.
 * Returns the translated string, or null if no message should be shown.
 */
function getSuccessMessage(actionType) {
  if (!(actionType in successMessage)) return null
  if (successMessage[actionType] === null) return null
  return i18n.t(`success.${actionType}`, { ns: "messages" })
}

// Action types that have an error message (looked up from locales/en/messages.json).
// `true` = has a translated message in messages:error.<key>
// `""` = no custom message (just shows the raw server error)
const errorMessage = {
  // Analysis
  "analyses/fetchUnplanned/ERROR": "",
  "analyses/fetchUnassignedSamples/ERROR": "",
  "analyses/fetchPlanned/ERROR": "",
  "analyses/remove/ERROR": true,

  // AnalysisDefinition
  "analysisDefinitions/fetch/ERROR": "",
  "analysisDefinitions/create/ERROR": true,
  "analysisDefinitions/remove/ERROR": true,

  // Asset
  "assets/fetchGraphInfo/ERROR": "",
  "assets/remove/ERROR": true,
  "assets/fetch/ERROR": "",
  "assets/create/ERROR": true,

  // Auth
  "auth/login/ERROR": true,

  // CompetenceGroup
  "competenceGroups/fetch/ERROR": "",
  "competenceGroups/create/ERROR": true,
  "competenceGroups/remove/ERROR": true,

  // Iap
  "iap/run/ERROR": true,
  "iap/runFillInSamples/ERROR": true,

  // Integration Sheets
  "integrationSheets/fetchActionSheets/ERROR": "",
  "integrationSheets/fetchProjectTemplateSheets/ERROR": "",
  "integrationSheets/fetchSampleSheets/ERROR": "",
  "integrationSheets/createActionSheet/ERROR": true,
  "integrationSheets/createProjectTemplateSheets/ERROR": true,
  "integrationSheets/createSampleSheet/ERROR": true,
  "integrationSheets/editActionSheet/ERROR": true,
  "integrationSheets/editProjectTemplateSheet/ERROR": true,
  "integrationSheets/editSampleSheet/ERROR": true,
  "integrationSheets/removeActionSheet/ERROR": true,
  "integrationSheets/removeProjectTemplateSheet/ERROR": true,
  "integrationSheets/removeSampleSheet/ERROR": true,

  // Loading
  "loading/fetchAll/ERROR": true,

  // Member
  "members/fetch/ERROR": "",
  "members/create/ERROR": true,
  "members/createAsAdmin/ERROR": true,
  "members/archive/ERROR": true,

  // Metrics
  "metrics/fetch/ERROR": "",

  // ProjectAction
  "projectActions/fetchUnplanned/ERROR": "",
  "projectActions/fetchPlanned/ERROR": "",
  "projectActions/move/ERROR": true,
  "projectActions/removePlanned/ERROR": true,
  "projectActions/setLocation/ERROR": true,
  "projectActions/setLocationUnplanned/ERROR": true,

  // Project
  "projects/fetch/ERROR": "",
  "projects/create/ERROR": true,
  "projects/remove/ERROR": true,
  "projects/delete-unplanned/ERROR": true,
  "projects/lockProject/ERROR": true,

  // ProjectTemplate
  "projectTemplates/fetch/ERROR": "",
  "projectTemplates/create/ERROR": true,
  "projectTemplates/createDraft/ERROR": true,
  "projectTemplates/remove/ERROR": true,

  // White tasks
  "whiteTasks/remove/ERROR": true,
  "whiteTasks/create/ERROR": true,
  "whiteTasks/fetch/ERROR": "",
  "whiteTasks/move/ERROR": "",

  // Analysis tasks
  "analysisTasks/fetch/ERROR": "",
  "analysisTasks/move/ERROR": "",

  // TeamStatus
  "teamStatus/fetchCurrent/ERROR": "",

  // CustomerAdministration
  "customers/deleteCustomer/ERROR": true,
  "customers/addCustomer/ERROR": true,
  "customers/editCustomer/ERROR": true,

  // Token
  "team/create-api-token/ERROR": true,
  "team/delete-api-token/ERROR": true,
}

/**
 * Look up translated error message for the given action type.
 * Returns the translated string, "" if no custom message, or null if not found.
 */
function getErrorMessage(actionType) {
  if (!(actionType in errorMessage)) return null
  if (errorMessage[actionType] === "") return ""
  return i18n.t(`error.${actionType}`, { ns: "messages" })
}

function getError({ type, err }) {
  // Don't show error messages for AbortError (page refresh) or DoNotShowError
  if (err && (err.name === 'AbortError' || err.name === 'DoNotShowError')) {
    return null
  }

  const customMsg = getErrorMessage(type)
  if (customMsg) {
    // We give the error a custom (translated) message based on the action the user tried to perform
    return `${customMsg}: ${err.message}`
  } else if (err) {
    // If an unhandled / un-customized error occurred
    return err.message
  }
  return null
}

function getTime(message) {
  // Kinda arbitary calculation to find how long to display a message
  return Math.max(message.length * 100, 3000)
}

const initialState = {
  message: null,
  time: null,
  variant: null,
}

export function reducer(state = initialState, action) {
  // Don't process AbortError - these are expected when page is refreshed
  // and should not trigger any error messages
  if (action.err && action.err.name === 'AbortError') {
    return state
  }

  // We do a bit out of the ordinary in this reducer, so that our switch statement doesn't get extremely large

  // Display success messages based on action type
  const successMsg = getSuccessMessage(action.type)
  if (successMsg) {
    return {
      message: successMsg,
      time: getTime(successMsg),
      variant: "success",
    }
  }

  // Display error message based on action type
  const error = getError(action)

  if (error) {
    return {
      message: action.err.name === "DoNotShowError" ? "DoNotShowError" : error,
      time: getTime(error),
      variant: action.err.name === "IAPinUseError" || action.err.name === "AnalysisUpdatedError" || action.type === "analyses/moveSmartIntoPlan/ERROR" ? "warning" : "danger",
    }
  }

  // Aaaand back to normal redux stuff again
  switch (action.type) {
    // TODO: Set `time` based on length of message?
    case "message/info":
      return {
        message: action.res,
        time: getTime(action.res),
        variant: "primary",
      }
    case "message/success":
      return {
        message: action.res,
        time: getTime(action.res),
        variant: "success",
      }
    case "message/warning":
      return {
        message: action.res,
        time: getTime(action.res),
        variant: "warning",
      }
    case "message/error":
      return {
        message: action.res,
        time: getTime(action.res),
        variant: "danger",
      }
    case "message/clear":
      return initialState
    default:
      return state
  }
}

export function info(message) {
  return { type: "message/info", res: message }
}

export function success(message) {
  return { type: "message/success", res: message }
}

export function warning(message) {
  return { type: "message/warning", res: message }
}

export function error(message) {
  return { type: "message/error", res: message }
}

export function clear() {
  return { type: "message/clear" }
}
